import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InteractiveElement } from "@iventis/styles/src/components";
import { isAdmin } from "@iventis/utilities";
import { Body2, Subtitle } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { Theme } from "@emotion/react";
import { findNode } from "@iventis/tree-browser/tree-operations";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Divider, IconButton } from "@mui/material";
import React, { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@iventis/styles";
import { inlineTextIconMargin } from "@iventis/styles/src/atomic-rules";
import { useTreeBrowser } from "@iventis/tree-browser";
import { DisabledOverlay, PermissionsToolTipComponent, SimpleTooltip } from "@iventis/components";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { MapGlobalState, SavedMapViewStages } from "@iventis/map-engine/src/state/map.state.types";
import { sidebar } from "./state/sidebar.slice";
import { rootStore } from "./state/root.store";
import { map } from "./state/map.slice";
import { setMapView } from "./map-views.helpers";

const MapViewsList: FunctionComponent = () => {
    const dispatch = useDispatch();

    const { SavedMapViews, savedMapViewDefault, mapId } = useSelector(({ mapReducer }: { mapReducer: MapGlobalState }) => ({
        SavedMapViews: mapReducer.savedMapViews,
        savedMapViewDefault: mapReducer.savedMapViewDefault,
        mapId: mapReducer.mapModule.mapId,
    }));

    // eslint-disable-next-line no-spaced-func, func-call-spacing
    const [{ tree }] = useTreeBrowser(TreeBrowserDomainNodeContext, null, "tree");
    const translate = useIventisTranslate();

    const isAdminOfMap = useMemo(() => isAdmin(findNode([tree], mapId)?.authorisation || Authorisation.None), [tree]);

    return (
        <Container>
            <SavedMapViewsHeading>
                <FontAwesomeIcon icon={["far", "camera"]} style={{ height: "16px", width: "16px", marginRight: inlineTextIconMargin }} />
                <Subtitle>{translate(Content.map.popup.saved_map_views)}</Subtitle>
            </SavedMapViewsHeading>
            <div className="search-results">
                {SavedMapViews &&
                    SavedMapViews.map((savedMapView) => (
                        <div className="search-result" key={savedMapView.id}>
                            <InteractiveElement
                                className="search-result-button"
                                onClick={() => {
                                    setMapView(savedMapView.id);
                                }}
                                data-testid={`saved-view-${savedMapView.name}`}
                            >
                                <Body2 style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                    {savedMapViewDefault === savedMapView.id ? <span>{`(${translate(Content.map.saved_map_view.map_default)}) `}</span> : ""}
                                    <span>{savedMapView.name}</span>
                                </Body2>
                            </InteractiveElement>
                            <IconButton
                                size="small"
                                edge="end"
                                className="search-result-edit-button"
                                disabled={!isAdminOfMap}
                                aria-label={translate(Content.map.saved_map_view.edit_saved_view)}
                                onClick={() => {
                                    rootStore.dispatch(map.actions.setSavedViewMapWizard({ stage: SavedMapViewStages.NOTIFICATION, id: savedMapView.id }));
                                    dispatch(sidebar.actions.closePopover());
                                    dispatch(sidebar.actions.setFullScreenSelector(null));
                                }}
                            >
                                <FontAwesomeIcon style={{ height: "16px", width: "16px" }} icon={["far", "pen-to-square"]} />
                            </IconButton>
                        </div>
                    ))}
            </div>
            <Divider />
            <SimpleTooltip text={<PermissionsToolTipComponent />} placement="right" disabled={isAdminOfMap}>
                <StyledCreateMapViewButton
                    style={{ position: "relative" }}
                    type="button"
                    disabled={!isAdminOfMap}
                    onClick={() => {
                        rootStore.dispatch(map.actions.setSavedViewMapWizard({ stage: SavedMapViewStages.NOTIFICATION, id: undefined }));
                        dispatch(sidebar.actions.closePopover());
                        dispatch(sidebar.actions.setFullScreenSelector(null));
                    }}
                    data-testid="map-create-saved-view-button"
                    cursor={isAdminOfMap ? "pointer" : "default"}
                >
                    {!isAdminOfMap && <DisabledOverlay />}
                    <Body2 style={{ textAlign: "left" }}>{translate(Content.map.saved_map_view.create_new_saved_view)}</Body2>
                </StyledCreateMapViewButton>
            </SimpleTooltip>
        </Container>
    );
};

const SavedMapViewsHeading = styled.div`
    display: flex;
    align-items: center;
    padding-bottom: 10px;
`;

const StyledCreateMapViewButton = styled(InteractiveElement)<{ cursor: "pointer" | "default" }>`
    padding: 12px 15px 12px 15px;
    width: 100%;

    :hover {
        background-color: ${({ theme }: { theme: Theme }) => theme.otherColors.hover};
        cursor: ${(props) => props.cursor};
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    overflow: hidden;

    .search-results {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        overflow-x: hidden;
    }

    .search-result {
        display: flex;
        border-radius: 3px;
        width: 100%;

        /* When hovering the main button — apply background to the whole row */
        .search-result-button:hover ~ .search-result-edit-button,
        .search-result-button:hover,
        .search-result-button:hover + * {
            /* no direct background here, handled below */
        }

        /* Apply hover background only when inside .search-result-button hover */
        .search-result-button:hover,
        .search-result-button:hover + .search-result-edit-button,
        &:has(.search-result-button:hover) {
            background-color: ${({ theme }) => theme.otherColors.hover};
        }
    }

    .search-result-button {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-grow: 1;
        flex-shrink: 1;
        padding: 10px 20px;
        cursor: pointer !important;
        background: transparent;
        border: none;
        border-radius: 3px 0px 0px 3px;
        // Max width is the full width minus the edit button width (44px) and right margin (5px)
        max-width: calc(100% - 49px);
    }

    .search-result-edit-button {
        flex-grow: 0;
        flex-shrink: 0;
        width: 44px;
        z-index: 1;
        background: transparent;
        margin-right: 5px;
        &:hover {
            background-color: ${({ theme }) => theme.otherColors.hover};
        }
    }
`;

export default MapViewsList;
